import React, { useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Download, Mail, Send, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const numberToWords = (num) => {
  if (!num || num === 0) return 'Zero Only';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
    'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const scales = ['','Thousand','Lakh','Crore'];
  const intPart = Math.floor(Math.abs(num));
  if (intPart === 0) return 'Zero Only';
  const convert = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n/10)] + ' ' + (n%10 ? ones[n%10] + ' ' : '');
    return ones[Math.floor(n/100)] + ' Hundred ' + (n%100 ? 'and ' + convert(n%100) : '');
  };
  const groups = [];
  let temp = intPart;
  groups.push(temp % 1000); temp = Math.floor(temp / 1000);
  while (temp > 0) { groups.push(temp % 100); temp = Math.floor(temp / 100); }
  let result = '';
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] !== 0) result += convert(groups[i]) + (scales[i] ? scales[i] + ' ' : '');
  }
  return 'Indian Rupee ' + result.trim() + ' Only';
};

const PayslipPDF = ({ payslip, onClose }) => {
  const contentRef = useRef(null);
  const [showEmail, setShowEmail] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState(false);

  const generatePdfBase64 = async () => {
    const el = contentRef.current;
    if (!el) return null;
    const worker = html2pdf().set({
      margin: [3, 3, 3, 3],
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    }).from(el);
    const pdf = await worker.outputPdf('arraybuffer');
    const bytes = new Uint8Array(pdf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };

  const downloadPDF = async () => {
    const el = contentRef.current;
    if (!el) return;
    await html2pdf().set({
      margin: [3, 3, 3, 3],
      filename: `Payslip_${payslip.employee_id || 'EMP'}_${payslip.month_name}_${payslip.year}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    }).from(el).save();
  };

  const sendEmail = async () => {
    if (!sendTo || !sendTo.includes('@')) { toast.error('Enter a valid email address'); return; }
    setSending(true);
    try {
      const pdfB64 = await generatePdfBase64();
      if (!pdfB64) { toast.error('Failed to generate PDF'); setSending(false); return; }
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/payroll/send-email`, {
        payslip_id: payslip.payslip_id,
        recipient_email: sendTo,
        pdf_base64: pdfB64
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Salary slip sent to ${sendTo}`);
      setShowEmail(false);
      setSendTo('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  const p = payslip;
  const doublePay = p.double_pay_amount || 0;
  const otherAddTotal = (p.other_additions||[]).reduce((s, a) => s + (a.amount||0), 0);
  const totalEarnings = (p.basic||0) + (p.hra||0) + (p.conveyance||0) + (p.medical||0) + (p.incentive||0) + doublePay + otherAddTotal;
  const otherDedTotal = (p.other_deductions||[]).reduce((s, d) => s + (d.amount||0), 0);
  const totalDeductions = (p.professional_tax||200) + otherDedTotal;
  const netPay = totalEarnings - totalDeductions;

  const dedRows = [{ name: 'Professional Tax', amount: p.professional_tax || 200 }];
  (p.other_deductions||[]).forEach(d => dedRows.push({ name: d.name, amount: d.amount }));

  const earnRows = [
    { name: 'Basic Salary', amount: p.basic },
    { name: 'House Rent Allowance', amount: p.hra },
    { name: 'Conveyance Allowance', amount: p.conveyance },
    { name: 'Medical Allowance', amount: p.medical },
  ];
  if (doublePay > 0) earnRows.push({ name: `Double Pay (${p.double_pay_days || 0} days)`, amount: doublePay });
  if (p.incentive > 0) earnRows.push({ name: 'Performance Incentive', amount: p.incentive });
  (p.other_additions||[]).forEach(a => { if (a.amount > 0) earnRows.push({ name: a.name, amount: a.amount }); });

  const maxRows = Math.max(earnRows.length, dedRows.length);

  /* Styles */
  const clr = { text: '#1a1a1a', muted: '#6b7280', light: '#9ca3af', border: '#e5e7eb', bg: '#ffffff', accent: '#059669' };
  const labelStyle = { padding: '4px 0', color: clr.muted, fontSize: '10px', width: '45%', verticalAlign: 'top' };
  const valStyle = { padding: '4px 0', color: clr.text, fontSize: '10px', fontWeight: '500', verticalAlign: 'top' };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white text-lg font-semibold">Salary Slip — {p.month_name} {p.year}</h3>
        <div className="flex gap-2">
          <Button onClick={() => setShowEmail(prev => !prev)}
            className={showEmail ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}
            data-testid="email-payslip-btn">
            <Mail className="w-4 h-4 mr-2" />Email
          </Button>
          <Button onClick={downloadPDF} className="bg-emerald-500 hover:bg-emerald-600" data-testid="download-payslip-btn">
            <Download className="w-4 h-4 mr-2" />Download PDF
          </Button>
          {onClose && <Button variant="outline" onClick={onClose}>Close</Button>}
        </div>
      </div>

      {showEmail && (
        <div className="flex items-center gap-2 mb-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600" data-testid="email-send-bar">
          <Mail className="w-4 h-4 text-teal-400 flex-shrink-0" />
          <Input type="email" value={sendTo} onChange={e => setSendTo(e.target.value)}
            placeholder="Enter recipient email address"
            className="flex-1 bg-slate-800 border-slate-600 text-white h-9 text-sm"
            data-testid="email-recipient-input" onKeyDown={e => e.key === 'Enter' && sendEmail()} />
          <Button onClick={sendEmail} disabled={sending} size="sm" className="bg-teal-500 hover:bg-teal-600 px-4" data-testid="send-email-btn">
            {sending ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Sending...</> : <><Send className="w-3.5 h-3.5 mr-2" />Send</>}
          </Button>
          <button onClick={() => { setShowEmail(false); setSendTo(''); }} className="text-slate-400 hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div style={{ background: '#fff', maxHeight: '72vh', overflow: 'auto', borderRadius: '6px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <div ref={contentRef} style={{
          width: '200mm', margin: '0 auto', padding: '28px 36px 20px',
          fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
          fontSize: '10px', color: clr.text, background: clr.bg, lineHeight: '1.5', boxSizing: 'border-box',
        }}>

          {/* ===== HEADER ===== */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '14px', borderBottom: `1px solid ${clr.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src={`${window.location.origin}/company-logo.png`} alt="" style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '4px' }} crossOrigin="anonymous" />
              <div style={{ fontSize: '20px', fontWeight: '700', color: clr.text, letterSpacing: '0.5px' }}>Creative Infotech</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '10px', color: clr.light }}>Payslip For the Month</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: clr.text, marginTop: '2px' }}>{p.month_name} {p.year}</div>
            </div>
          </div>

          {/* ===== EMPLOYEE SUMMARY + NET PAY ===== */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
            {/* Left: Employee Details */}
            <div style={{ flex: '1 1 55%' }}>
              <div style={{ fontSize: '9px', fontWeight: '700', color: clr.muted, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>Employee Summary</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    ['Employee Name', p.agent_name || '—'],
                    ['Designation', p.designation || '—'],
                    ['Employee ID', p.employee_id || '—'],
                    ['Date of Joining', p.date_of_joining || '—'],
                    ['Pay Period', `${p.month_name} ${p.year}`],
                    ['Bank A/C', p.bank_account || '—'],
                  ].map(([label, val], i) => (
                    <tr key={i}>
                      <td style={labelStyle}>{label}</td>
                      <td style={{ ...labelStyle, width: '5%', textAlign: 'center' }}>:</td>
                      <td style={valStyle}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Right: Net Pay Callout + Days */}
            <div style={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '16px 20px', textAlign: 'left', width: '100%', marginBottom: '12px' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: clr.accent, lineHeight: '1.1' }}>
                  ₹{fmt(netPay)}
                </div>
                <div style={{ fontSize: '10px', color: clr.accent, marginTop: '4px', fontWeight: '500' }}>Employee Net Pay</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ ...labelStyle, width: '55%' }}>Paid Days</td>
                    <td style={{ ...labelStyle, width: '5%', textAlign: 'center' }}>:</td>
                    <td style={{ ...valStyle, fontWeight: '700' }}>{p.paid_days}</td>
                  </tr>
                  <tr>
                    <td style={labelStyle}>LOP Days</td>
                    <td style={{ ...labelStyle, width: '5%', textAlign: 'center' }}>:</td>
                    <td style={{ ...valStyle, fontWeight: '700' }}>{p.lop_days || 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== SEPARATOR ===== */}
          <div style={{ height: '1px', background: clr.border, marginBottom: '16px' }}></div>

          {/* ===== EARNINGS & DEDUCTIONS TABLE ===== */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${clr.border}` }}>
                <th style={{ padding: '8px 0', textAlign: 'left', fontSize: '9px', fontWeight: '700', color: clr.muted, textTransform: 'uppercase', letterSpacing: '1px', width: '35%' }}>Earnings</th>
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: '9px', fontWeight: '700', color: clr.muted, textTransform: 'uppercase', letterSpacing: '1px', width: '15%' }}>Amount</th>
                <th style={{ padding: '8px 0', textAlign: 'left', fontSize: '9px', fontWeight: '700', color: clr.muted, textTransform: 'uppercase', letterSpacing: '1px', width: '35%', paddingLeft: '24px' }}>Deductions</th>
                <th style={{ padding: '8px 0', textAlign: 'right', fontSize: '9px', fontWeight: '700', color: clr.muted, textTransform: 'uppercase', letterSpacing: '1px', width: '15%' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxRows }).map((_, i) => {
                const e = earnRows[i];
                const d = dedRows[i];
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${clr.border}` }}>
                    <td style={{ padding: '8px 0', fontSize: '10px', color: clr.text }}>{e?.name || ''}</td>
                    <td style={{ padding: '8px 0', fontSize: '10px', color: clr.text, textAlign: 'right', fontWeight: '600' }}>{e ? `₹${fmt(e.amount)}` : ''}</td>
                    <td style={{ padding: '8px 0', fontSize: '10px', color: clr.text, paddingLeft: '24px' }}>{d?.name || ''}</td>
                    <td style={{ padding: '8px 0', fontSize: '10px', color: clr.text, textAlign: 'right', fontWeight: '600' }}>{d ? `₹${fmt(d.amount)}` : ''}</td>
                  </tr>
                );
              })}
              {/* Totals Row */}
              <tr style={{ borderTop: `2px solid ${clr.border}` }}>
                <td style={{ padding: '10px 0', fontSize: '10px', fontWeight: '700', color: clr.text }}>Gross Earnings</td>
                <td style={{ padding: '10px 0', fontSize: '11px', fontWeight: '800', color: clr.text, textAlign: 'right' }}>₹{fmt(totalEarnings)}</td>
                <td style={{ padding: '10px 0', fontSize: '10px', fontWeight: '700', color: clr.text, paddingLeft: '24px' }}>Total Deductions</td>
                <td style={{ padding: '10px 0', fontSize: '11px', fontWeight: '800', color: clr.text, textAlign: 'right' }}>₹{fmt(totalDeductions)}</td>
              </tr>
            </tbody>
          </table>

          {/* ===== SPACER ===== */}
          <div style={{ height: '16px' }}></div>

          {/* ===== TOTAL NET PAYABLE ===== */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '14px', paddingTop: '14px', borderTop: `1px solid ${clr.border}` }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '800', color: clr.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Net Payable</div>
              <div style={{ fontSize: '9px', color: clr.light, marginTop: '2px' }}>Gross Earnings - Total Deductions</div>
            </div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: clr.text }}>₹{fmt(netPay)}</div>
          </div>

          {/* ===== AMOUNT IN WORDS ===== */}
          <div style={{ textAlign: 'center', marginBottom: '20px', paddingTop: '10px' }}>
            <span style={{ fontSize: '9px', color: clr.light }}>Amount In Words : </span>
            <span style={{ fontSize: '9.5px', color: clr.accent, fontWeight: '600' }}>{numberToWords(Math.round(netPay))}</span>
          </div>

          {/* ===== FOOTER ===== */}
          <div style={{ textAlign: 'center', paddingTop: '12px', borderTop: `1px solid ${clr.border}` }}>
            <div style={{ fontSize: '8px', color: clr.light }}>
              This document has been automatically generated, therefore no signature is required.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayslipPDF;
